import type {
  SupportAttachment,
  SupportConversationDetail,
  SupportMessage,
} from '@recipes/contracts';
import { Alert, Badge, Button, cn, Label, Spinner, Textarea } from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Paperclip, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/forms/confirm-dialog';
import { toast } from '@/components/feedback/toaster';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatBytes, formatDate } from '@/features/shared/labels';
import { api } from '@/lib/api';
import { useIsAdmin } from '@/lib/auth/auth-provider';
import { describeError, isNotFound, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { MAIL_STATUS_VARIANT } from './mail-delivery-page';
import { SUPPORT_STATUS_VARIANT } from './support-page';

const draftKey = (id: string) => `rm.support.draft.${id}`;
const readDraft = (id: string): string => {
  try {
    return localStorage.getItem(draftKey(id)) ?? '';
  } catch {
    return '';
  }
};
const writeDraft = (id: string, text: string): void => {
  try {
    if (text) localStorage.setItem(draftKey(id), text);
    else localStorage.removeItem(draftKey(id));
  } catch {
    // storage unavailable: the draft lives as long as the page
  }
};

const selectClass =
  'h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-2';

/** One support conversation (docs/partial/email-and-support-spec.md 5.3, 6.6). */
export function AdminSupportConversationPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const conversation = useQuery({
    queryKey: queryKeys.admin.supportConversation(id),
    queryFn: () => api.admin.support.conversation(id),
    enabled: Boolean(id),
    retry: false,
  });
  return (
    <AdminPage section="support" title={conversation.data?.subject}>
      <Link
        to="/admin/support"
        className="mb-4 inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
      >
        <ArrowLeft aria-hidden className="size-4" />
        {t('admin.support.back')}
      </Link>
      {conversation.isLoading && <Spinner label={t('app.loading')} />}
      {conversation.error && (
        <Alert variant="destructive">
          {isNotFound(conversation.error)
            ? t('admin.support.notFound')
            : describeError(conversation.error)}
        </Alert>
      )}
      {conversation.data && <Conversation data={conversation.data} />}
    </AdminPage>
  );
}

function Conversation({ data }: { data: SupportConversationDetail }) {
  const { t, i18n } = useTranslation();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [spamOpen, setSpamOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const accept = (next: SupportConversationDetail) => {
    queryClient.setQueryData(queryKeys.admin.supportConversation(data.id), next);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'conversations'] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.admin.supportUnread });
  };

  // Opening a conversation is reading it.
  const markedRead = useRef(false);
  useEffect(() => {
    if (!data.unread || markedRead.current) return;
    markedRead.current = true;
    api.admin.support.update(data.id, { unread: false }).then(accept, () => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id, data.unread]);

  const staff = useQuery({
    queryKey: queryKeys.admin.supportStaff,
    queryFn: () => api.admin.support.staff(),
    staleTime: 5 * 60_000,
  });
  const update = useMutation({
    mutationFn: (body: Parameters<typeof api.admin.support.update>[1]) =>
      api.admin.support.update(data.id, body),
    onSuccess: accept,
    onError: (e) => toastError(e),
  });
  const gone = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'support'] });
    navigate('/admin/support');
  };
  const spam = useMutation({
    mutationFn: () => api.admin.support.markSpam(data.id),
    onSuccess: () => {
      toast.success(t('admin.support.spamDone'));
      gone();
    },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: () => api.admin.support.remove(data.id),
    onSuccess: () => {
      toast.success(t('admin.support.deleted'));
      gone();
    },
    onError: (e) => toastError(e),
  });
  const lastInbound = [...data.messages].reverse().find((m) => m.direction === 'IN');
  const untrusted = lastInbound?.authResult ? !lastInbound.authResult.trusted : false;

  return (
    <div className="space-y-4" data-testid="support-conversation">
      <header className="flex flex-wrap items-center gap-2">
        <Badge variant={SUPPORT_STATUS_VARIANT[data.status]} data-testid="support-status">
          {t(`admin.support.statuses.${data.status}`)}
        </Badge>
        <span className="text-sm text-muted-foreground">S-{data.number}</span>
        <span className="text-sm">
          {data.customerName ? `${data.customerName} · ` : ''}
          {data.customerAddress}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Label htmlFor="support-assignee" className="sr-only">
            {t('admin.support.assignedTo')}
          </Label>
          <select
            id="support-assignee"
            className={selectClass}
            value={data.assignedTo?.id ?? ''}
            disabled={update.isPending}
            onChange={(e) => update.mutate({ assignedToId: e.target.value || null })}
            data-testid="support-assignee"
          >
            <option value="">{t('admin.support.unassigned')}</option>
            {staff.data?.map((member) => (
              <option key={member.id} value={member.id}>
                {member.displayName}
              </option>
            ))}
          </select>
          {data.status === 'CLOSED' ? (
            <Button variant="outline" size="sm" onClick={() => update.mutate({ status: 'OPEN' })}>
              {t('admin.support.reopen')}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => update.mutate({ status: 'CLOSED' })}
              data-testid="support-close"
            >
              {t('admin.support.close')}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSpamOpen(true)}
            data-testid="support-spam"
          >
            {t('admin.support.spam')}
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              aria-label={t('admin.support.delete')}
              data-testid="support-delete"
            >
              <Trash2 aria-hidden />
            </Button>
          )}
        </div>
      </header>

      {data.account ? (
        <section
          aria-labelledby="support-account"
          className="rounded-lg border bg-card p-3 text-sm"
          data-testid="support-account"
        >
          <h2 id="support-account" className="font-medium">
            {t('admin.support.account')}
          </h2>
          <p className="mt-1">
            {isAdmin ? (
              <Link
                to={`/admin/users/${data.account.id}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {data.account.displayName}
              </Link>
            ) : (
              data.account.displayName
            )}{' '}
            · {data.account.email} · {t(`enums.userStatus.${data.account.status}`)} ·{' '}
            {t(data.account.emailVerified ? 'admin.support.verified' : 'admin.support.notVerified')}{' '}
            · {data.account.locale.toUpperCase()} ·{' '}
            {t('admin.support.since', { date: formatDate(data.account.createdAt, i18n.language) })}
          </p>
          <p
            className={cn(
              'mt-2 flex items-start gap-1.5',
              untrusted ? 'text-destructive' : 'text-muted-foreground',
            )}
            data-testid="support-account-hint"
          >
            {untrusted ? (
              <ShieldAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
            ) : (
              <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
            )}
            {t(untrusted ? 'admin.support.accountUntrusted' : 'admin.support.accountHint')}
          </p>
        </section>
      ) : (
        <p className="text-sm text-muted-foreground">{t('admin.support.noAccount')}</p>
      )}

      <ol className="space-y-3" aria-label={t('admin.support.messagesLabel')}>
        {data.messages.map((message) => (
          <Message key={message.id} message={message} />
        ))}
      </ol>

      <ReplyBox conversation={data} onSent={accept} />

      <ConfirmDialog
        open={spamOpen}
        onOpenChange={setSpamOpen}
        title={t('admin.support.spamTitle')}
        description={t('admin.support.spamDescription', { address: data.customerAddress })}
        confirmLabel={t('admin.support.spam')}
        destructive
        loading={spam.isPending}
        onConfirm={() => spam.mutate()}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('admin.support.deleteTitle')}
        description={t('admin.support.deleteDescription')}
        confirmLabel={t('admin.support.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}

/** What a stranger's HTML may do inside its frame: show itself – nothing else. */
const FRAME_POLICY =
  '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src https:; style-src \'unsafe-inline\'">';

function Message({ message }: { message: SupportMessage }) {
  const { t, i18n } = useTranslation();
  const inbound = message.direction === 'IN';
  const system = message.kind === 'SYSTEM';
  const [formatted, setFormatted] = useState(false);
  const [images, setImages] = useState(false);
  const html = useQuery({
    queryKey: ['admin', 'support', 'html', message.id, images],
    queryFn: () => api.admin.support.html(message.id, images),
    enabled: formatted,
    staleTime: Infinity,
  });
  const auth = message.authResult;
  return (
    <li
      className={cn(
        'rounded-lg border p-3',
        system
          ? 'mx-auto max-w-xl bg-muted/40 text-center text-sm text-muted-foreground'
          : inbound
            ? 'mr-6 bg-card'
            : 'ml-6 bg-accent/40',
      )}
      data-testid={`support-message-${message.direction}`}
    >
      {system ? (
        <p>
          {t('admin.support.acknowledged')} · {formatDate(message.createdAt, i18n.language, true)}{' '}
          <Badge variant={MAIL_STATUS_VARIANT[message.status]}>
            {t(`admin.mailDelivery.statuses.${message.status}`)}
          </Badge>
        </p>
      ) : (
        <>
          <div className="mb-2 flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">
              {inbound ? message.fromAddress : (message.sentByName ?? t('admin.support.team'))}
            </span>
            <span>{formatDate(message.createdAt, i18n.language, true)}</span>
            {inbound && auth && (
              <Badge variant={auth.trusted ? 'success' : 'destructive'} data-testid="support-auth">
                {auth.trusted
                  ? t('admin.support.authOk')
                  : t('admin.support.authFailed', {
                      detail: `SPF ${auth.spf ?? '–'} · DKIM ${auth.dkim ?? '–'} · DMARC ${auth.dmarc ?? '–'}`,
                    })}
              </Badge>
            )}
            {inbound && message.hasHtml && (
              <button
                type="button"
                className="ml-auto text-primary underline-offset-2 hover:underline"
                onClick={() => setFormatted((value) => !value)}
              >
                {t(formatted ? 'admin.support.showText' : 'admin.support.showFormatted')}
              </button>
            )}
          </div>
          {formatted ? (
            <div>
              {html.isLoading && <Spinner label={t('app.loading')} />}
              {html.data && (
                <>
                  {html.data.blockedImages > 0 && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      {t('admin.support.imagesBlocked', { count: html.data.blockedImages })}{' '}
                      <button
                        type="button"
                        className="text-primary underline-offset-2 hover:underline"
                        onClick={() => setImages(true)}
                      >
                        {t('admin.support.loadImages')}
                      </button>
                    </p>
                  )}
                  <iframe
                    title={message.subject}
                    // No scripts, no forms, no same-origin access; links open outside the frame.
                    sandbox="allow-popups allow-popups-to-escape-sandbox"
                    referrerPolicy="no-referrer"
                    srcDoc={`${FRAME_POLICY}<base target="_blank">${html.data.html}`}
                    className="h-96 w-full rounded border bg-paper"
                  />
                </>
              )}
            </div>
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm">{message.text}</p>
          )}
          {message.attachments.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2">
              {message.attachments.map((attachment) => (
                <Attachment key={attachment.id} attachment={attachment} />
              ))}
            </ul>
          )}
          {!inbound && (
            <p className="mt-2 text-xs" data-testid="support-delivery">
              <Badge variant={MAIL_STATUS_VARIANT[message.status]}>
                {t(`admin.mailDelivery.statuses.${message.status}`)}
              </Badge>
              {message.error && <span className="ml-2 text-destructive">{message.error}</span>}
            </p>
          )}
        </>
      )}
    </li>
  );
}

function Attachment({ attachment }: { attachment: SupportAttachment }) {
  const { t } = useTranslation();
  const download = useMutation({
    mutationFn: () => api.admin.support.attachment(attachment.id),
    // A download with a generic type – the browser never renders what a stranger sent.
    onSuccess: (link) => window.location.assign(link.url),
    onError: (e) => toastError(e),
  });
  const label = `${attachment.fileName} · ${formatBytes(attachment.sizeBytes)}`;
  if (!attachment.stored)
    return (
      <li className="flex items-center gap-1.5 rounded-md border border-dashed px-2 py-1 text-xs text-muted-foreground">
        <Paperclip aria-hidden className="size-3.5" /> {label} · {t('admin.support.notStored')}
      </li>
    );
  return (
    <li>
      <Button
        variant="outline"
        size="sm"
        loading={download.isPending}
        onClick={() => download.mutate()}
      >
        <Download aria-hidden /> {label}
      </Button>
    </li>
  );
}

function ReplyBox({
  conversation,
  onSent,
}: {
  conversation: SupportConversationDetail;
  onSent: (next: SupportConversationDetail) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => readDraft(conversation.id));
  const [quote, setQuote] = useState(false);
  useEffect(() => writeDraft(conversation.id, text), [conversation.id, text]);
  const send = useMutation({
    mutationFn: (close: boolean) =>
      api.admin.support.reply(conversation.id, { text: text.trim(), quote, close }),
    onSuccess: (next) => {
      setText('');
      setQuote(false);
      onSent(next);
      toast.success(t('admin.support.sent'));
    },
    onError: (e) => toastError(e),
  });
  const empty = text.trim().length === 0;
  return (
    <section
      aria-labelledby="support-reply"
      className="sticky bottom-0 rounded-lg border bg-background p-3 shadow-sm"
    >
      <Label id="support-reply" htmlFor="support-reply-text">
        {t('admin.support.reply', { address: conversation.customerAddress })}
      </Label>
      <Textarea
        id="support-reply-text"
        className="mt-1"
        rows={5}
        maxLength={20000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="support-reply-text"
      />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={quote} onChange={(e) => setQuote(e.target.checked)} />
          {t('admin.support.quote')}
        </label>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            disabled={empty}
            loading={send.isPending && send.variables === true}
            onClick={() => send.mutate(true)}
            data-testid="support-send-close"
          >
            {t('admin.support.sendAndClose')}
          </Button>
          <Button
            disabled={empty}
            loading={send.isPending && send.variables === false}
            onClick={() => send.mutate(false)}
            data-testid="support-send"
          >
            {t('admin.support.send')}
          </Button>
        </div>
      </div>
    </section>
  );
}
